"use client";
import ToggleSet from "./toggles/toggleSet";
import StyleToggleSet from "./toggles/styleToggleSet";
import AdvancedToggleSet from "./toggles/advancedToggleSet";
import { useCallback, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { gifGenerator } from "../shared/generators/generators";
import { isSDXL } from "../shared/generators/isSDXL";
import { useGlobalContext } from "@/app/context/store";
import { Tag, TagGroup } from "@/app/context/generatorStore";
import TagCounter from "./toggles/tagCounter";
import Feedback from "./toggles/feedback";
import OtherSites from "./toggles/otherSites";
import ContentRequest from "./toggles/contentRequest";
import Competition from "./toggles/competition";
import SecondaryMenu from "./generatorActions/secondaryMenu/secondaryMenu";
import MenuBar from "./generatorActions/menuBar/menuBar";
import Display from "./generatorViewPort/display/display";
import {
  getGifModel,
  getModel,
  startGenerate,
  startGenerateGif,
  getUserModel,
} from "@/lib/api/generate";
import DiscordStatsComponent from "./socialComponent/serverStats";
import RedditComponent from "./socialComponent/reddit";
import { getLocalizedPath } from "@/components/shared/formatURL";
import { Dices } from "lucide-react";
import Link from "next/link";
import { useSignInModal } from "../layout/sign-in-modal";
import { ActiveTab, DisplayLoadingState, useGenerator } from "@/app/context/GeneratorContext";
import {
  getRandomTagCount,
  hasEnoughCredits,
  transformGifTags,
  transformTags,
  updateTogglesList,
} from "@/lib/utils/generation";
import JobHistory from "./generatorViewPort/history/jobHistory";
import GenerationTabs from "./toggles/generationTabs";
import GemCostToast from "../toast/GemCostToast";
import makeToast from "@/lib/utils/makeToast";
import BanModal from "@/components/ban/banModal";

import AdvancedToggleSetGif from "./toggles/advancedToggleSetGif";
import { saveTags } from "@/lib/api/tag";
import { getResolutions } from "@/lib/utils/getResolutions";
import { useGenerationRulesModal } from "./toggles/generationRulesModal";
import GenerationDown from "../shared/flags/generationDown";
import { useFlags } from "@/lib/hooks/useFlags";

interface GenerationProps {
  translations: any;
  signInModalTranslations: any;
  menuTranslations: any;
  displayTranslations: any;
  historyTranslations: any;
  toggleTranslations: any;
  socialTranslations: any;
  banTranslations: any;
  termsTranslations: any;
  flagTranslations: any;
}

export default function Generation({
  translations,
  signInModalTranslations,
  menuTranslations,
  displayTranslations,
  historyTranslations,
  toggleTranslations,
  socialTranslations,
  banTranslations,
  termsTranslations,
  flagTranslations,
}: GenerationProps) {
  const { user, fetchUser, updateCredits } = useGlobalContext();
  const { jobHistory } = useGenerator();
  const { data: flagsData } = useFlags();
  const generationFlagEnabled = flagsData?.["generation-down"]?.enabled;

  const {
    state,
    generatorList,
    generatorListSuccess,
    tagList,
    gifTagList,
    tagListLoading,
    activeTab,
    displayLoadingState,
    displayMedia,
    setDisplayMedia,
    batchSize,
    generateStartLoading,
    setGenerateStartLoading,
    setTagListLoading,
    setSelectedGenerator,
    setTagList,
    setGifTagList,
    setCustomPrompt,
    setResolution,
    setDisplayLoadingState,
    setDetectCelebrity,
    setCreditCost,
    setActiveJobRef,
    setQueuePosition,
    setGeneratingQueueType,
    refetchJobHistory,
  } = useGenerator();

  const router = useRouter();
  const searchParams = useSearchParams();

  const { SignInModal, setShowSignInModal } = useSignInModal(signInModalTranslations);

  // const isMobile = !useMediaQuery(getBreakpoint("sm"));

  // const toasterContainerStyle = useMemo(
  //   () =>
  //     isMobile
  //       ? {
  //           position: "absolute" as const,
  //           left: 0,
  //           transform: "translateY(-25px)",
  //         }
  //       : ({
  //           position: "absolute" as const,
  //           left: "100%",
  //           bottom: "0",
  //           marginLeft: "8px",
  //         } as CSSProperties),
  //   [isMobile],
  // );

  const [showBanModal, setShowBanModal] = useState(false);
  const [pressed, setPressed] = useState<"gamba" | "generate">("generate");
  const { setShowGenerationRulesModal, GenerationRulesModal } = useGenerationRulesModal(
    termsTranslations,
    pressed,
    () => {
      if (state.generationType === "image") {
        gamba();
      } else if (state.generationType === "gif") {
        gambaGif();
      }
    },
    () => {
      if (state.generationType === "image") {
        startGenerator(tagList);
      } else if (state.generationType === "gif") {
        startGifGenerator(gifTagList);
      }
    },
  );

  //randomizer
  const gamba = useCallback(() => {
    let newTagList = [...tagList];

    newTagList.forEach((tagGroup) => {
      //@ts-ignore
      let indices = [];
      //@ts-ignore
      const maxCount = tagGroup.tags.length;
      const numberOfTagsToSelect = getRandomTagCount(maxCount);

      if (numberOfTagsToSelect > 0) {
        //@ts-ignore
        if (tagGroup.type === "single") {
          //@ts-ignore
          indices.push(Math.floor(Math.random() * tagGroup.tags.length));
          //@ts-ignore
        } else if (tagGroup.type === "multiple") {
          //@ts-ignore
          const shuffledIndices = tagGroup.tags.map((_, index) => index);
          for (let i = shuffledIndices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffledIndices[i], shuffledIndices[j]] = [shuffledIndices[j], shuffledIndices[i]];
          }
          indices = shuffledIndices.slice(0, numberOfTagsToSelect);
        }
      }
      //@ts-ignore
      tagGroup.tags.forEach((tag) => {
        tag.selected = false;
      });
      //@ts-ignore
      indices.forEach((index) => {
        //@ts-ignore
        tagGroup.tags[index].selected = true;
      });
    });

    // Count total selected tags
    let totalSelected = newTagList.reduce(
      (acc, group) => acc + group.tags.filter((tag) => tag.selected).length,
      0,
    );

    // Deselect tags randomly if total selected exceeds 6
    while (totalSelected > 6) {
      for (let tagGroup of newTagList) {
        if (totalSelected <= 6) break; // Stop if we have deselected enough tags
        for (let tag of tagGroup.tags) {
          // Randomly deselect a tag if selected
          if (tag.selected && Math.random() < 0.5) {
            tag.selected = false;
            totalSelected--;
            if (totalSelected <= 6) break; // Stop if enough tags are deselected
          }
        }
      }
    }

    setTagList(newTagList);
    startGenerator(newTagList);
  }, [user, state, tagList, batchSize]);

  const gambaGif = useCallback(() => {
    let newTagList = [...gifTagList];

    // Count total available tags
    const totalAvailableTags = newTagList.reduce((acc, group) => acc + group.tags.length, 0);

    // If we have no tags available, return early
    if (totalAvailableTags === 0) return;

    // First pass - try to select tags normally
    newTagList.forEach((tagGroup) => {
      //@ts-ignore
      let indices = [];
      //@ts-ignore
      const maxCount = tagGroup.tags.length;
      const numberOfTagsToSelect = getRandomTagCount(maxCount);

      if (numberOfTagsToSelect > 0) {
        //@ts-ignore
        if (tagGroup.type === "single") {
          //@ts-ignore
          indices.push(Math.floor(Math.random() * tagGroup.tags.length));
          //@ts-ignore
        } else if (tagGroup.type === "multiple") {
          //@ts-ignore
          const shuffledIndices = tagGroup.tags.map((_, index) => index);
          for (let i = shuffledIndices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffledIndices[i], shuffledIndices[j]] = [shuffledIndices[j], shuffledIndices[i]];
          }
          indices = shuffledIndices.slice(0, numberOfTagsToSelect);
        }
      }
      //@ts-ignore
      tagGroup.tags.forEach((tag) => {
        tag.selected = false;
      });
      //@ts-ignore
      indices.forEach((index) => {
        //@ts-ignore
        tagGroup.tags[index].selected = true;
      });
    });

    // Count total selected tags
    let totalSelected = newTagList.reduce(
      (acc, group) => acc + group.tags.filter((tag: any) => tag.selected).length,
      0,
    );

    // If no tags were selected and we have available tags, force select at least one
    if (totalSelected === 0 && totalAvailableTags > 0) {
      // Find first group with tags and select one randomly
      for (let tagGroup of newTagList) {
        if (tagGroup.tags.length > 0) {
          const randomIndex = Math.floor(Math.random() * tagGroup.tags.length);
          tagGroup.tags[randomIndex].selected = true;
          totalSelected = 1;
          break;
        }
      }
    }

    // Continue with existing logic to cap at 6 tags
    while (totalSelected > 6) {
      for (let tagGroup of newTagList) {
        if (totalSelected <= 6) break; // Stop if we have deselected enough tags
        for (let tag of tagGroup.tags) {
          // Randomly deselect a tag if selected
          if (tag.selected && Math.random() < 0.5) {
            tag.selected = false;
            totalSelected--;
            if (totalSelected <= 6) break; // Stop if enough tags are deselected
          }
        }
      }
    }

    setGifTagList(newTagList);
    startGifGenerator(newTagList);
  }, [user, state, gifTagList, batchSize]);

  const startGenerator = useCallback(
    async (tagList: TagGroup[]) => {
      window.scrollTo(0, 0);
      if (user.banned?.level === 2) {
        setShowBanModal(true);
        return;
      }

      if (!hasEnoughCredits(user, state, tagList)) {
        setDisplayLoadingState(DisplayLoadingState.ErrorOutOfCredits);
        return;
      }

      setDetectCelebrity(false);
      setDisplayMedia([]);

      let customPrompt = state.customPrompt;
      let customNegative = state.customNegative;

      let selectedTagGroups: TagGroup[] = [];

      tagList.forEach((tagGroup) => {
        let selectedTags = tagGroup.tags
          .filter((tag: Tag) => tag.selected)
          .map((tag) => ({ id: tag.id, name: tag.name } as Tag));

        if (selectedTags.length > 0) {
          selectedTagGroups.push({
            id: tagGroup.id,
            name: tagGroup.name,
            type: tagGroup.type,
            tags: selectedTags,
            tagIds: selectedTags.map((tag) => tag.id),
          });
        }
      });

      setActiveJobRef(null);
      setQueuePosition("");
      setGeneratingQueueType(state.queueType);
      const body = {
        batch: batchSize,
        generator: state.selectedGenerator.shortName,
        tagGroups: selectedTagGroups,
        customPrompt: customPrompt,
        queueType: state.queueType,
        resolution: state.resolution,
        ...(state.customNegative !== "" && {
          customNegative: customNegative,
        }),
        ...(state.cfgScale !== 0 && { cfgScale: state.cfgScale }),
      };
      try {
        setGenerateStartLoading(true);
        setDisplayLoadingState(DisplayLoadingState.Queueing);
        const startGenerateRes = await startGenerate(body);

        if (startGenerateRes?.response?.shadowBannedPrompt) {
          setDetectCelebrity(true);
        }

        if (startGenerateRes?.error) {
          if (startGenerateRes.error.response?.status === 429) {
            setDisplayLoadingState(DisplayLoadingState.ErrorJobLimit);
          } else if (startGenerateRes.error.response?.status === 402) {
            setDisplayLoadingState(DisplayLoadingState.ErrorOutOfCredits);
          } else {
            setDisplayLoadingState(DisplayLoadingState.ErrorGeneric);
          }
          return;
        }

        if (startGenerateRes?.response?.jobId) {
          setActiveJobRef({
            jobId: startGenerateRes?.response?.jobId,
            delayedUntil: startGenerateRes?.response?.delay,
          });
          updateCredits({
            ccBill: startGenerateRes.response.creditUsage.ccBillChange,
            subStar: startGenerateRes.response.creditUsage.subStarChange,
            niche: startGenerateRes.response.creditUsage.nicheChange,
            daily: startGenerateRes.response.creditUsage.dailyChange,
          });
          if (startGenerateRes.response.creditUsage.creditCost > 0) {
            makeToast(<GemCostToast amount={startGenerateRes.response.creditUsage.creditCost} />, {
              duration: 4500,
            });
          }
        }
      } catch (error) {
        console.error("Generation error:", error);
        setActiveJobRef(null);
        setDisplayLoadingState(DisplayLoadingState.ErrorGeneric);
        setDisplayMedia([]);
      } finally {
        setGenerateStartLoading(false);
        // invalidate job history
        refetchJobHistory();
      }
    },
    [user, state, tagList, batchSize],
  );

  const startGifGenerator = useCallback(
    async (tagList: TagGroup[]) => {
      window.scrollTo(0, 0);
      if (user.banned?.level === 2) {
        setShowBanModal(true);
        return;
      }

      if (!hasEnoughCredits(user, state, tagList)) {
        setDisplayLoadingState(DisplayLoadingState.ErrorOutOfCredits);
        return;
      }

      setDetectCelebrity(false);
      setDisplayMedia([]);

      let customPrompt = state.customPromptGif;

      let tags: any[] = [];

      tagList.forEach((tagGroup) => {
        let selectedTags = tagGroup.tags
          .filter((tag: Tag) => tag.selected)
          .map((tag) => ({ id: tag.id, name: tag.name } as Tag));

        for (let t of selectedTags) {
          tags.push(t.id);
        }
      });

      setActiveJobRef(null);
      setQueuePosition("");
      setGeneratingQueueType(state.queueType);
      const body = {
        generator: gifGenerator, //"6795cbf25b8a7b9c59b42e77",
        tags: tags,
        customPrompt: customPrompt,
        queueType: 2,
        aspectRatio: state.gifAspectRatio,
        length: state.gifLength,
      };
      try {
        setGenerateStartLoading(true);
        setDisplayLoadingState(DisplayLoadingState.Queueing);
        const startGenerateRes = await startGenerateGif(body);

        if (startGenerateRes?.response?.shadowBannedPrompt) {
          setDetectCelebrity(true);
        }

        if (startGenerateRes?.error) {
          if (startGenerateRes.error.response?.status === 429) {
            setDisplayLoadingState(DisplayLoadingState.ErrorJobLimit);
          } else if (startGenerateRes.error.response?.status === 402) {
            setDisplayLoadingState(DisplayLoadingState.ErrorOutOfCredits);
          } else {
            setDisplayLoadingState(DisplayLoadingState.ErrorGeneric);
          }
          return;
        }

        if (startGenerateRes?.response?.jobId) {
          setActiveJobRef({
            jobId: startGenerateRes?.response?.jobId,
            delayedUntil: startGenerateRes?.response?.delay,
          });
          updateCredits({
            ccBill: startGenerateRes.response.creditUsage.ccBillChange,
            subStar: startGenerateRes.response.creditUsage.subStarChange,
            niche: startGenerateRes.response.creditUsage.nicheChange,
            daily: startGenerateRes.response.creditUsage.dailyChange,
          });
          if (startGenerateRes.response.creditUsage.creditCost > 0) {
            makeToast(<GemCostToast amount={startGenerateRes.response.creditUsage.creditCost} />, {
              duration: 4500,
            });
          }
        }
      } catch (error) {
        console.error("Generation error:", error);
        setActiveJobRef(null);
        setDisplayLoadingState(DisplayLoadingState.ErrorGeneric);
        setDisplayMedia([]);
      } finally {
        setGenerateStartLoading(false);
        // invalidate job history
        refetchJobHistory();
      }
    },
    [user, state, tagList, batchSize],
  );

  const handleGeneratePressed = () => {
    if (user._id === "none") {
      setShowSignInModal(true);
    } else if (user.agreedGenerationRules === false || user.agreedGenerationRules === undefined) {
      setShowGenerationRulesModal(true);
      setPressed("generate");
    } else {
      if (state.generationType === "image") {
        startGenerator(tagList);
      } else if (state.generationType === "gif") {
        startGifGenerator(gifTagList);
      }
    }
  };

  const handleGambaClick = () => {
    if (user._id === "none") {
      setShowSignInModal(true);
    } else if (user.agreedGenerationRules === false || user.agreedGenerationRules === undefined) {
      setShowGenerationRulesModal(true);
      setPressed("gamba");
    } else {
      if (state.generationType === "image") {
        gamba();
      } else if (state.generationType === "gif") {
        gambaGif();
      }
    }
  };

  const addTags = async (otherTags: string[]) => {
    try {
      await saveTags(otherTags);
    } catch (error) {
      console.error("Failed to save tag", error);
    }
    //await fetchUser();
  };

  useEffect(() => {
    (async () => {
      window.scrollTo(0, 0);
      setTagListLoading(true);
      if (generatorListSuccess) {
        // check localStorage for cached generator
        const cachedGeneratorId = localStorage.getItem("selectedGenerator");
        const cachedGenerator = cachedGeneratorId
          ? generatorList.find((generator) => generator._id === cachedGeneratorId)
          : null;

        // TODO: look into what this does lol
        // does nothing display image always "" ?
        const style =
          state.displayImage === "" && displayLoadingState !== DisplayLoadingState.Generating
            ? searchParams.get("style")
            : null;
        const tags =
          state.displayImage === "" && displayLoadingState !== DisplayLoadingState.Generating
            ? searchParams.get("tags")?.split(",") || []
            : [];
        const prompt =
          state.displayImage === "" && displayLoadingState !== DisplayLoadingState.Generating
            ? searchParams.get("prompt")
            : null;
        const resolution =
          state.displayImage === "" && displayLoadingState !== DisplayLoadingState.Generating
            ? searchParams.get("resolution")
            : null;

        if (isSDXL(style || "style")) {
          setResolution(10);
        }

        if (prompt) {
          if (user._id !== "none" ? true : false) {
            setCustomPrompt(prompt);
          }
        }

        if (resolution) {
          const isPony = isSDXL(style || "style");
          const resolutions = getResolutions(isPony);
          const paramRes = resolutions.find((res) => resolution === res.string);
          const resValue = paramRes?.value || (isPony ? 10 : 1);
          if (user._id !== "none" ? true : false) {
            if (
              (resValue === 4 ||
                resValue === 5 ||
                resValue === 6 ||
                resValue === 8 ||
                resValue === 9) &&
              (user.membership?.level === 1 ? false : true)
            ) {
              //premium value with no premium membership
              setResolution(isPony ? 10 : 1);
            } else {
              //has premium membership or not a premium value
              setResolution(resValue);
            }
          } else {
            setResolution(isPony ? 10 : 1);
          }
        }

        if (style === null && state.selectedGenerator._id == "") {
          // FIX THIS EZ
          // Use cached generator if available, otherwise use first on the list
          const generatorToUse = cachedGenerator || generatorList[0];
          setSelectedGenerator(generatorToUse);
          let model = await getModel(generatorToUse._id);

          if (isSDXL(generatorToUse.shortName)) {
            setResolution(10);
          }
          let listOfTogglesJSON = transformTags(
            model.response?.data?.tempTagGroups,
            Number(translations.index),
          );
          setCreditCost(model.response?.data?.creditCost);
          // If NLI user, skip getting userModel and load only the default model to display
          if (user._id !== "none") {
            let userModel = await getUserModel(); // User preferences
            const userHiddenTags = userModel.response?.data?.tags.hidden;
            const userSavedTags = userModel.response?.data?.tags.saved;

            // Update toggles list with user preferences from userModel
            const updatedListOfToggles = updateTogglesList(
              listOfTogglesJSON,
              userSavedTags,
              userHiddenTags,
              Number(translations.index),
            );

            setTagList(updatedListOfToggles);
          } else {
            setTagList(listOfTogglesJSON);
          }
        } else if (style != null) {
          // If directed to /create through cloning
          const selectedGenerator = generatorList.find(
            (generator) => generator.shortName === style,
          );
          if (selectedGenerator) {
            setSelectedGenerator(selectedGenerator);
            let model = await getModel(selectedGenerator._id);

            let listOfTogglesJSON = transformTags(
              model.response?.data?.tempTagGroups,
              Number(translations.index),
            );

            setCreditCost(model.response?.data?.creditCost);

            // If NLI user, skip getting userModel
            if (user._id !== "none") {
              let userModel = await getUserModel(); // User preferences
              const userHiddenTags = userModel.response?.data?.tags.hidden;
              const userSavedTags = userModel.response?.data?.tags.saved;
              // Update toggles list with user preferences from userModel
              const updatedListOfToggles = updateTogglesList(
                listOfTogglesJSON,
                userSavedTags,
                userHiddenTags,
                Number(translations.index),
              );

              let newListOfTogglesJSON = JSON.parse(JSON.stringify(updatedListOfToggles));
              //Filter out tags outside of default and user preferences
              const allTags = new Set(
                newListOfTogglesJSON.flatMap((tempTagGroup: TagGroup) =>
                  tempTagGroup.tags.map((tag) => tag.id),
                ),
              );

              const tagsToAdd = tags.filter((tagId) => !allTags.has(tagId));
              //Add tags from cloning post not existing in users current preferences
              if (tagsToAdd.length > 0) {
                await addTags(tagsToAdd);
                model = await getModel(selectedGenerator._id);
                listOfTogglesJSON = transformTags(
                  model.response?.data?.tempTagGroups,
                  Number(translations.index),
                );
                newListOfTogglesJSON = JSON.parse(JSON.stringify(listOfTogglesJSON));
              }
              //@ts-ignore
              newListOfTogglesJSON.forEach((item) => {
                //@ts-ignore
                item.tags.forEach((tag) => {
                  if (tags.includes(tag.id)) {
                    tag.selected = true;
                  }
                });
              });
              setTagList(newListOfTogglesJSON);
            } else {
              // Set to default list if NLI user
              setTagList(listOfTogglesJSON);
            }
          } else {
            // Handle the case where the generator with the specified ID (style) is not found
            console.error(`Generator with ID ${style} not found.`);
          }
        }
        setTagListLoading(false);
        // Properly clear search params by pushing a new URL without params
        if (searchParams.toString()) {
          router.push(window.location.pathname);
        }
      }
    })();
  }, [searchParams, user, generatorListSuccess]);

  useEffect(() => {
    const loadGifTags = async () => {
      if (state.generationType === "gif" && !tagListLoading && !gifTagList.length) {
        setTagListLoading(true);
        let gifModel = await getGifModel();
        let newListOfTogglesJSON = transformGifTags(
          gifModel.response?.data?.tagGroups,
          Number(translations.index),
        );
        setGifTagList(newListOfTogglesJSON);
        setTagListLoading(false);
      }
    };

    loadGifTags();
  }, [state.generationType]);

  const isTagSelected = tagList.reduce((acc, tagSet) => {
    if (acc) return true;
    return tagSet?.tags?.some((tag) => tag.selected);
  }, false);

  return (
    <>
      <div className="relative mb-auto mt-[-23px] w-full max-w-[95em] sm:mt-0">
        <SignInModal />
        <BanModal
          isOpen={showBanModal}
          onClose={() => setShowBanModal(false)}
          translations={banTranslations}
        />
        <GenerationRulesModal />
        <div className="sm:px-[16px] md:px-8">
          {/* <div className="w-full md:w-8/12 mx-auto mb-4 rounded-xl border-[0.5px] border-[#3C3C3C] bg-[#2B2D31] p-4 text-center text-sm font-medium text-white shadow-sm">
            {translations.maintenance}
          </div> */}
          <GenerationDown message={flagTranslations.generationDown} />

          {user._id !== "none" && jobHistory?.length > 0 && (
            <GenerationTabs translations={translations} />
          )}
          {activeTab === ActiveTab.History && <JobHistory translations={historyTranslations} />}
          {activeTab === ActiveTab.Generation && (
            <>
              <Display translations={displayTranslations} />
              <div className="mx-auto mt-5 w-full flex-col xl:w-8/12">
                <SecondaryMenu translations={menuTranslations} banTranslations={banTranslations} />
                <div className="mb-4 shadow-sm">
                  <MenuBar
                    translations={menuTranslations}
                    signInModalTranslations={signInModalTranslations}
                  />
                  {state.generationType === "image" ? (
                    <>
                      {" "}
                      <StyleToggleSet translations={toggleTranslations} />
                      <AdvancedToggleSet translations={toggleTranslations} />
                      {/* <BatchToggleSet />
                  <ResToggleSet /> */}
                    </>
                  ) : (
                    <>
                      {" "}
                      <AdvancedToggleSetGif translations={toggleTranslations} />
                      {/* <BatchToggleSet />
                <ResToggleSet /> */}
                    </>
                  )}
                </div>
                {state.generationType === "image" ? (
                  <>
                    {" "}
                    <>
                      <TagCounter tagCost={state.creditCost?.tagCost} translations={translations} />
                      <div className={`${tagList.length > 0 ? "block" : "hidden"}`}>
                        {tagList.map((tagSet: any, i: number) => {
                          return (
                            <ToggleSet
                              key={i}
                              currentIndex={i}
                              totalIndex={tagList.length}
                              title={tagSet.name}
                              id={tagSet.id}
                              listOfToggles={tagSet.tags}
                              type={tagSet.type}
                              generationType="image"
                              isSelected={isTagSelected}
                              translations={toggleTranslations}
                              signInModalTranslations={signInModalTranslations}
                            />
                          );
                        })}
                      </div>
                      <div
                        className={`${
                          tagList.length === 0 ? "block" : "hidden"
                        } mb-4 border-[0.5px] border-[#3C3C3C] bg-[#2B2D31] px-2 py-2 sm:rounded-lg md:px-4`}
                      >
                        <div className="border-b-0 px-[12px] py-2 text-center text-sm font-bold text-white sm:px-0">
                          {translations.loading}
                        </div>
                      </div>
                    </>
                  </>
                ) : (
                  <>
                    {" "}
                    <div className={`${gifTagList.length > 0 ? "block" : "hidden"}`}>
                      {gifTagList.map((tagSet: any, i: number) => {
                        return (
                          <ToggleSet
                            key={i}
                            currentIndex={i}
                            totalIndex={gifTagList.length}
                            title={tagSet.name}
                            id={tagSet.id}
                            listOfToggles={tagSet.tags}
                            type={tagSet.type}
                            generationType="gif"
                            isSelected={false}
                            translations={toggleTranslations}
                            signInModalTranslations={signInModalTranslations}
                          />
                        );
                      })}
                    </div>
                    <div
                      className={`${
                        gifTagList.length === 0 ? "block" : "hidden"
                      } mb-4 border-[0.5px] border-[#3C3C3C] bg-[#2B2D31] px-2 py-2 sm:rounded-lg md:px-4`}
                    >
                      <div className="border-b-0 px-[12px] py-2 text-center text-sm font-bold text-white sm:px-0">
                        {translations.loading}
                      </div>
                    </div>
                  </>
                )}
                <div className="mb-4 w-full border-[0.5px] border-[#3C3C3C] bg-[#2B2D31] p-4 pt-2 text-center text-sm font-medium text-white shadow-sm sm:rounded-xl">
                  <div className="flex w-full flex-col sm:flex-row">
                    <div className="w-full sm:w-8/12">
                      <DiscordStatsComponent translations={socialTranslations} />
                    </div>
                    <div className="flex w-full sm:w-4/12">
                      <RedditComponent translations={socialTranslations} />
                    </div>
                  </div>
                </div>
                <Competition translations={socialTranslations} />
                <ContentRequest translations={socialTranslations} />
                <Feedback translations={socialTranslations} />
                <OtherSites />
                <div className="fixed bottom-0 left-1/2 flex h-[6.25rem] w-full translate-x-[-50%] bg-gradient-to-t from-[#131315] via-[#131315]/70 to-transparent sm:bottom-16 sm:h-auto sm:w-auto sm:bg-none">
                  <div className="relative mx-auto mb-auto flex items-center hover:brightness-105">
                    <button
                      // disabled
                      disabled={
                        user._id === "none"
                          ? false
                          : generateStartLoading || tagListLoading || generationFlagEnabled
                      }
                      onClick={() => handleGeneratePressed()}
                      className={`${
                        user._id === "none"
                          ? "rounded-full bg-[#DD0772] py-1.5"
                          : generateStartLoading || tagListLoading || generationFlagEnabled
                          ? "rounded-full border-[0.5px] border-[#3C3C3C] bg-[#2B2D31] py-1.5"
                          : "rounded-full bg-[#DD0772] py-1.5"
                      } overflow-hidden whitespace-nowrap px-4 text-sm font-bold text-white shadow-2xl`}
                    >
                      {generateStartLoading ? translations.generating : translations.generate}
                    </button>
                    <Link
                      href={getLocalizedPath("upgrade") + "?ref=gen"}
                      className={`${
                        user.membership && user?.membership.level === 1 ? "hidden" : "block"
                      } ml-2 overflow-hidden whitespace-nowrap rounded-full bg-[#FFCC18] px-4 py-2 text-sm font-bold text-black shadow-2xl hover:brightness-105`}
                    >
                      {translations.upgrade}
                    </Link>
                    <button
                      // disabled
                      aria-label="Generate With Random Tags"
                      className={`${"bg-[#fff] py-1.5"} ml-2 rounded-full px-4 text-sm font-bold text-white shadow-2xl`}
                      disabled={
                        tagListLoading || generateStartLoading || generationFlagEnabled
                          ? true
                          : false
                      }
                      onClick={() => {
                        handleGambaClick();
                      }}
                    >
                      <Dices
                        color={`${
                          tagListLoading || generateStartLoading || generationFlagEnabled
                            ? "#2B2D31"
                            : "#DD0772"
                        }`}
                        className="h-5 w-auto"
                      />
                    </button>
                    {/* <Toaster
                      containerStyle={toasterContainerStyle}
                      position="bottom-left"
                      gutter={16}
                      toastOptions={{
                        duration: 4500,
                      }}
                    /> */}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
